import {
  IMachineContext,
  IStateMetaData,
  IYardageSelectionEvent,
  TInputTypes,
  TMachineActions,
  TMachineEvents,
  TMachineStates,
  TPlayResults,
  TRefEventStates,
  TWorkflowType,
} from '@/types';
import type { AnyEventObject, BaseActionObject, StateNodeConfig } from 'xstate';
import { TPATPlayEvents, TPATStates } from '@/types/workflow/patMachine';
import { TFieldGoalEvents } from '@/types/workflow/fieldGoalMachine';
import { MAIN_MACHINE_ID } from '@/constants';
import { PAT_REF_WF } from './refEventFlows/pat';
import { handleOnePointSafteyConfirmation, handleTwoPointReturnConfirmation } from '../utils';
import {
  on2ptAttempt,
  on2ptAttemptSnap,
  onPatConfirmed,
  onPatDesignSelected,
  onPatSnapped,
  onXpAttempt,
} from './nextActions/pat';

export const PAT_MACHINE: StateNodeConfig<IMachineContext, any, AnyEventObject, BaseActionObject> = {
  initial: TPATStates.PAT_PLAY_DESIGN,
  states: {
    [TPATStates.PAT_PLAY_DESIGN]: {
      meta: {
        component: TInputTypes.TWO_ROW_GENERIC,
        componentConfiguration: {
          title: 'PAT',
          showLogo: true,
          showBackBtn: false,
        },
        onNext: onPatDesignSelected,
        onBack({ send, context }) {
          if (context.inputForNextComponent.backTo === TFieldGoalEvents.TO_START) {
            send(TFieldGoalEvents.TO_START, {
              ctxUpdate: {
                currentWorkflow: TWorkflowType.FIELD_GOAL,
                currentPlay: null,
              },
            });
          }
        },
      } as IStateMetaData,
      on: {
        [TFieldGoalEvents.TO_START]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.INIT_FIELD_GOAL}`,
          cond: (context) => context.inputForNextComponent.backTo === TFieldGoalEvents.TO_START,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TPATPlayEvents.TO_TWO_PT_ATTEMPT]: {
          target: TPATStates.TWO_PT_ATTEMPT_SNAP,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TPATPlayEvents.TO_XP_ATTEMPT]: {
          target: TPATStates.XP_ATTEMPT_SNAP,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TPATPlayEvents.REF_EVENT]: {
          target: TPATStates.REF_EVENT,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
      },
    },

    [TPATStates.XP_ATTEMPT_SNAP]: {
      meta: {
        component: TInputTypes.TWO_ROW_GENERIC,
        componentConfiguration: {
          title: 'XP Attempt',
          showLogo: true,
          showBackBtn: true,
        },
        onNext: onPatSnapped,
        onBack({ send }) {
          send(TPATPlayEvents.BACK, {});
        },
      } as IStateMetaData,
      on: {
        [TPATPlayEvents.BACK]: {
          target: TPATStates.PAT_PLAY_DESIGN,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TPATPlayEvents.TO_XP_ATTEMPT]: {
          target: TPATStates.XP_ATTEMPT,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TPATPlayEvents.REF_EVENT]: {
          target: TPATStates.REF_EVENT,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
      },
    },

    [TPATStates.XP_ATTEMPT]: {
      meta: {
        component: TInputTypes.TWO_ROW_GENERIC,
        componentConfiguration: {
          title: 'XP Attempt',
          showLogo: true,
          showBackBtn: true,
        },
        onNext: onXpAttempt,
        onBack({ send }) {
          send(TPATPlayEvents.BACK, {});
        },
      } as IStateMetaData,
      on: {
        [TPATPlayEvents.BACK]: {
          target: TPATStates.XP_ATTEMPT_SNAP,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TPATPlayEvents.PAT_PENALTY]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.PAT}.${TPATStates.REF_EVENT}.${TRefEventStates.PENALTY}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TPATPlayEvents.PAT_COMPLETED]: {
          target: TPATStates.CONFIRM_PAT,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
      },
    },

    [TPATStates.TWO_PT_ATTEMPT_SNAP]: {
      meta: {
        component: TInputTypes.TWO_ROW_GENERIC,
        componentConfiguration: {
          title: '2PT Attempt',
          showLogo: true,
          showBackBtn: true,
        },
        onNext: on2ptAttemptSnap,
        onBack({ send }) {
          send(TPATPlayEvents.BACK, {});
        },
      } as IStateMetaData,
      on: {
        [TPATPlayEvents.BACK]: {
          target: TPATStates.PAT_PLAY_DESIGN,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TPATPlayEvents.TO_TWO_PT_ATTEMPT]: {
          target: TPATStates.TWO_PT_ATTEMPT,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TPATPlayEvents.REF_EVENT]: {
          target: TPATStates.REF_EVENT,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
      },
    },

    [TPATStates.TWO_PT_ATTEMPT]: {
      meta: {
        component: TInputTypes.TWO_ROW_GENERIC,
        componentConfiguration: {
          title: '2PT Attempt',
          showLogo: true,
          showBackBtn: true,
        },
        onNext: on2ptAttempt,
        onBack({ send }) {
          send(TPATPlayEvents.BACK, {});
        },
      } as IStateMetaData,
      on: {
        [TPATPlayEvents.BACK]: {
          target: TPATStates.TWO_PT_ATTEMPT_SNAP,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TPATPlayEvents.PAT_COMPLETED]: {
          target: TPATStates.CONFIRM_PAT,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TPATPlayEvents.PAT_PENALTY]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.PAT}.${TPATStates.REF_EVENT}.${TRefEventStates.PENALTY}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TPATPlayEvents.ON_TWO_PT_RETURN_ONE_PT_SAFTEY]: {
          target: TPATStates.TWO_PT_RETURN_ONE_PT_SAFTEY,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
      },
    },

    [TPATStates.TWO_PT_RETURN_ONE_PT_SAFTEY]: {
      meta: {
        component: TInputTypes.TWO_ROW_GENERIC,
        componentConfiguration: {
          title: '2PT Attempt',
          showLogo: true,
          showBackBtn: true,
        },
        onNext({ payload, send, playFlowState, context }) {
          if (payload.value === TPlayResults.ONE_PT_SAFTEY) {
            handleOnePointSafteyConfirmation(send, context, playFlowState);
          } else if (payload.value === TPlayResults.TWO_PT_RETURN) {
            handleTwoPointReturnConfirmation(send, context, playFlowState);
          }
        },
        onBack({ send }) {
          send(TPATPlayEvents.BACK, {});
        },
      } as IStateMetaData,
      on: {
        [TPATPlayEvents.BACK]: {
          target: TPATStates.TWO_PT_ATTEMPT,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TPATPlayEvents.PAT_COMPLETED]: {
          target: TPATStates.CONFIRM_PAT,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
      },
    },

    [TPATStates.REF_EVENT]: PAT_REF_WF,

    [TPATStates.SELECT_YARDAGE]: {
      meta: {
        component: TInputTypes.YARDAGE_SELECTOR,
        componentConfiguration: {
          title: 'Post-Penalty Yardage',
          showLogo: true,
          showBackBtn: true,
        },
        onNext({ send, context, payload }) {
          const yardageEvt = payload as IYardageSelectionEvent;

          send(TMachineEvents.PLAY_COMPLETED, {
            ctxUpdate: {
              ...context,
              currentPlay: {
                ...context.currentPlay,
                postPlayYardLine: yardageEvt.yardLine,
                postPlaySide: yardageEvt.side,
              },
            } as IMachineContext,
            nextStateInput: {
              backTo: TPATPlayEvents.BACK_TO_YARDAGE,
            },
          });
        },
        onBack({ send }) {
          send(TPATPlayEvents.BACK, {});
        },
      } as IStateMetaData,
      on: {
        [TMachineEvents.PLAY_COMPLETED]: {
          target: TPATStates.CONFIRM_PAT,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TMachineEvents.BACK]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.PAT}.${TPATStates.REF_EVENT}.${TRefEventStates.PENALTY}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
      },
    },

    [TPATStates.CONFIRM_PAT]: {
      meta: {
        component: TInputTypes.CONFIRMATION_SELECTOR,
        componentConfiguration: {
          title: 'Confirm PAT',
          showLogo: false,
          showBackBtn: true,
        },
        onNext: onPatConfirmed,
        onBack({ send, context }) {
          send(context.inputForNextComponent.backTo, {});
        },
      } as IStateMetaData,
      on: {
        [TPATPlayEvents.BACK_TO_XP_ATTEMPT]: {
          target: TPATStates.XP_ATTEMPT,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TPATPlayEvents.BACK_TO_TWO_PT_ATTEMPT]: {
          target: TPATStates.TWO_PT_ATTEMPT,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TPATPlayEvents.BACK_TO_TWO_PT_RETURN_ONE_PT_SAFTEY]: {
          target: TPATStates.TWO_PT_RETURN_ONE_PT_SAFTEY,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TMachineEvents.ON_REF_EVENT]: {
          target: TPATStates.REF_EVENT,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TPATPlayEvents.PAT_COMPLETED]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.INIT_KICKOFF}`,
          actions: [
            TMachineActions.UPDATE_CONTEX,
            TMachineActions.SET_NEXT_STATE_INPUT,
            TMachineActions.SAVE_PLAY,
          ],
        },
        [TPATPlayEvents.OVERTIME_PAT_COMPLETE]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.IDLE}`,
          actions: [
            TMachineActions.UPDATE_CONTEX,
            TMachineActions.SET_NEXT_STATE_INPUT,
            TMachineActions.SAVE_PLAY,
          ],
        },
        [TMachineEvents.TO_PAT]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.PAT}`,
          actions: [
            TMachineActions.UPDATE_CONTEX,
            TMachineActions.SET_NEXT_STATE_INPUT,
            TMachineActions.SAVE_PLAY,
          ],
        },
        [TPATPlayEvents.TO_GAME_OVER]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.GAME_OVER}`,
          actions: [
            TMachineActions.UPDATE_CONTEX,
            TMachineActions.SET_NEXT_STATE_INPUT,
            TMachineActions.SAVE_PLAY,
          ],
        },
      },
    },
  },
};
